import { supabase } from '../components/data/supabaseClient';
import { styleStaticMapUrl } from './mapStyle';

/**
 * Fetch a Google Static Maps image ONCE (at publish time) and store it in
 * Supabase Storage, returning the public URL — so the feed serves our own
 * stored image instead of re-billing Google Static Maps on every card view.
 *
 * The browser fetch runs with the app's referrer, so the existing
 * referrer-restricted Maps key works (a server-side fetch would be rejected by
 * that same restriction). If anything fails (CORS, network, storage), it
 * gracefully returns the original styled Google URL — so there is never a
 * regression, only a missed optimization. Never throws.
 *
 * @param {string} rawStaticMapUrl - the googleapis staticmap URL from the route form
 * @param {string} userId - the publishing user's id (uploads go to their folder)
 * @returns {Promise<string|null>} stored public URL, or the styled Google URL as fallback
 */
export async function storeRouteMapImage(rawStaticMapUrl, userId) {
  const styledUrl = styleStaticMapUrl(rawStaticMapUrl);

  // Only handle Google static-map URLs; pass anything else straight through.
  if (!styledUrl || !styledUrl.includes('maps.googleapis.com/maps/api/staticmap') || !userId) {
    return styledUrl || rawStaticMapUrl || null;
  }

  try {
    const res = await fetch(styledUrl);
    if (!res.ok) return styledUrl;

    const blob = await res.blob();
    if (!blob.type.startsWith('image/') || blob.size === 0 || blob.size > 1048576) {
      return styledUrl;
    }

    const ext = blob.type.includes('jpeg') ? 'jpg' : blob.type.includes('webp') ? 'webp' : 'png';
    const path = `${userId}/${crypto.randomUUID()}.${ext}`;

    const { error } = await supabase.storage
      .from('post-maps')
      .upload(path, blob, { contentType: blob.type, cacheControl: '31536000', upsert: false });

    if (error) {
      console.warn('Route-map storage failed, falling back to Google URL:', error.message);
      return styledUrl;
    }

    const { data } = supabase.storage.from('post-maps').getPublicUrl(path);
    return data?.publicUrl || styledUrl;
  } catch (e) {
    console.warn('Route-map storage error, falling back to Google URL:', e?.message || e);
    return styledUrl;
  }
}
